import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { buildSameOriginApiUrl, UrlLocation } from './utils/url-helper';

export type AccessoryNetworkCommandType = 'SET_ACCESSORY_NETWORK_ACTUATOR_STATE';

export interface DeviceCommand {
  serialNumber?: number;
  group: number | null;
  type: number | null;
  accessoryId: number;
  action: string;
}

export interface CommandRequest {
  userId: number;
  veoid: number;
  type: AccessoryNetworkCommandType;
  deviceCommand: DeviceCommand;
}

export interface CommandResult {
  status?: number;
  message?: string;
  commandTicket?: string;
  command?: {
    commandTicket?: string;
  };
  data?: unknown;
}

export function resolveCommandApiBaseUrl(
  fallbackUrl: string,
  loc: UrlLocation = globalThis.location,
): string {
  const isAbsoluteUrl = /^https?:\/\//i.test(fallbackUrl);
  if (isAbsoluteUrl) {
    return fallbackUrl;
  }

  const isRelativePath = fallbackUrl.startsWith('/');
  if (isRelativePath) {
    return buildSameOriginApiUrl(fallbackUrl, loc);
  }

  return fallbackUrl;
}

export function resolveCommandsEndpoint(baseUrl: string): string {
  let normalizedBaseUrl = baseUrl;
  while (normalizedBaseUrl.endsWith('/')) {
    normalizedBaseUrl = normalizedBaseUrl.slice(0, -1);
  }

  if (normalizedBaseUrl.endsWith('/commands')) {
    return normalizedBaseUrl;
  }

  return `${normalizedBaseUrl}/commands`;
}

@Injectable({ providedIn: 'root' })
export class CommandService {
  private readonly commandApiUrl = environment.commandApiUrl;
  private readonly commandPlatform = environment.commandPlatform;

  constructor(private readonly http: HttpClient) {}

  sendCommand(payload: CommandRequest, token?: string): Observable<CommandResult> {
    const headersConfig: Record<string, string> = {
      plataforma: this.commandPlatform,
    };

    if (token) {
      headersConfig['Authorization'] = `Bearer ${token}`;
    }

    const headers = new HttpHeaders(headersConfig);
    return this.http.post<CommandResult>(this.resolveCommandsApiUrl(), payload, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 409) {
          return of({
            status: 409,
            message: error.error?.message,
            commandTicket: error.error?.commandTicket,
            command: error.error?.command,
            data: error.error,
          } as CommandResult);
        }
        return throwError(() => error);
      }),
    );
  }

  deleteCommandTicket(ticketId: string, token?: string): Observable<void> {
    const headersConfig: Record<string, string> = {
      plataforma: this.commandPlatform,
    };

    if (token) {
      headersConfig['Authorization'] = `Bearer ${token}`;
    }

    const headers = new HttpHeaders(headersConfig);
    return this.http.delete<void>(`${this.resolveCommandsApiUrl()}/tickets/${ticketId}`, { headers });
  }

  private resolveCommandsApiUrl(): string {
    const baseUrl = resolveCommandApiBaseUrl(this.commandApiUrl);
    return resolveCommandsEndpoint(baseUrl);
  }
}
